import cn from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";

import React, { Fragment, useMemo } from "react";

import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Box, Button, Chip, LinearProgress, Tooltip, Typography } from "@mui/material";

import SitemapDialogBtn from "@/components/SitemapDialog";
import { AIRPORT_MAP } from "@/constants/airport";
import { CabinNameMap, CabinsColors } from "@/constants/config";
import usePlatform from "@/hooks/usePlatform";
import { FlightItem } from "@/typings";
import { calculateTargetUrl } from "@/utils";

interface SummaryAirlineProps {
  data: FlightItem;
  showLastSeen?: boolean;
  onSearch?: (data: FlightItem) => void;
  showSearchBtn?: boolean;
}

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s",
    // s: "seconds",
    // m: "1 minute",
    // mm: "%d minutes",
    s: "Real-time",
    m: "minutes ago",
    mm: "minutes ago",
    h: "1 hour ago",
    hh: "%d hours ago",
    d: "1 day ago",
    dd: "%d days ago",
    M: "1 month ago",
    MM: "%d months ago",
    y: "1 year ago",
    yy: "%d years ago",
  },
});

const formatTime = (minutes: number) => {
  if (minutes > 60) {
    return `${Math.floor(minutes / 60)}hr ${minutes % 60}min`;
  } else {
    return `${minutes} min`;
  }
};

const SummaryAirline = ({
  data,
  showLastSeen = false,
  showSearchBtn,
  onSearch,
}: SummaryAirlineProps) => {
  const { isPc } = usePlatform();
  const products = useMemo(() => data?.fare?.products || [], [data]);
  const lastSeen = useMemo(() => data?.ls, [data]);
  const seats = useMemo(() => data?.ss, [data]);
  const stopInfo = useMemo(() => {
    return `${data.fare.travel_time_display.replace("h", " hr").replace("m", " min")}${` - `}${products.length === 1 ? "Nonstop" : `${products.length - 1} stop`
      }`;
  }, [data, products]);

  // Check if last seen is less than 1 minute
  const isLastSeenLessThanMinute = useMemo(() => {
    return dayjs().unix() - lastSeen < 15;
  }, [lastSeen]);

  const airportInfo = useMemo(() => {
    return [products[0]?.origin, ...products.map((ele) => ele.destination)].join(" - ");
  }, [products]);

  const airportTipsInfo = [
    `${AIRPORT_MAP[products[0]?.origin]?.name} (${products ? products[0]?.origin : ""})`,
    ...products.map((item) => {
      return `${AIRPORT_MAP[item.destination]?.name} (${item.destination})`;
    }),
  ];

  const buy_ticket_url = useMemo(() => {
    const products = data?.fare?.products || [];
    const origin_airport = products[0]?.origin;
    const arrival_airport = products[products.length - 1]?.destination;
    const departure_date = data?.date;
    const program_code = data?.program_code;
    const cabin = data?.payment?.cabin || data?.final_cabin_type;
    return calculateTargetUrl(
      program_code,
      origin_airport,
      arrival_airport,
      departure_date,
      cabin,
      true
    );
  }, [data]);

  const totalMinutes = useMemo(() => {
    return products.reduce((t, cur) => {
      return t + Math.round(cur.travel_minutes);
    }, 0);
  }, [products]);

  return (
    <Box
      className={cn("flex", " flex-col", {
        "items-center": true,
      })}
    >
      <Typography variant="body1" color="textPrimary">
        {stopInfo}
      </Typography>

      <Tooltip
        title={
          <div>
            <div className="flex flex-col mb-[20px]">
              {airportTipsInfo.map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </div>
            <div className="flex flex-col">
              {products.map((item, index) => (
                <Fragment key={index}>
                  <div>
                    <strong>✈️ {item.cabin_type}</strong> ({item.origin} - {item.destination}):{" "}
                    {formatTime(item.travel_minutes)} -{" "}
                    {`${Math.round((item.travel_minutes / totalMinutes) * 100)}%`}
                  </div>
                  {products.length - 1 > index && (
                    <div className="text-center my-[3px]"> 🕒 Layover: {formatTime(item.layover_time)}</div>
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        }
        placement="top"
        arrow
      >
        <div className="flex flex-col items-center">
          <Typography variant="body2" color="textSecondary">
            {airportInfo}
          </Typography>
          <div className="flex space-x-[5px] py-[10px] w-[110px]">
            {products.map((item, index) => (
              <div
                key={index}
                style={{
                  flex: Math.round((item.travel_minutes / totalMinutes) * 100),
                  height: 2.5,
                  backgroundColor: CabinsColors[CabinNameMap[item.cabin_type as CabinsType] as keyof typeof CabinsColors] || CabinsColors.economy,
                }}
                className="flex-1 rounded-sm w-full"
              />
            ))}
          </div>
        </div>
      </Tooltip>

      {data.cash_fare > 0 && (
        <div className="flex items-center ml-[-2px]">
          <Button
            sx={{ borderRadius: 2, paddingX: 1.5 }}
            size="small"
            href={buy_ticket_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MonetizationOnOutlinedIcon color="primary" sx={{ fontSize: 15, mt: 0.3 }} />
            <Typography variant="body2" color="textSecondary" className="pt-[2px] pl-[2px]">
              Cash ${data.cash_fare.toLocaleString()}
            </Typography>
          </Button>
        </div>
      )}
{/* 
      {showLastSeen ? (
        <Typography variant="button" color="textSecondary">
          Seen: {dayjs(lastSeen * 1000).fromNow()}
        </Typography>
      ) : (
        ""
      )} */}



      {data.isTop && (
        <Chip
          label="Panorama Flight Match"
          color="primary"
          size="small"
          icon={<StarBorderIcon color="primary" />}
        />
      )}

      {isPc && <div className="mt-[-4px]">
        <SitemapDialogBtn data={data} />
      </div>}
      {showSearchBtn && !isLastSeenLessThanMinute ? (
        <Button
          component="label"
          variant="contained"
          color="primary"
          sx={{ borderRadius: "50px", marginTop: "4px", width: "240px" }}
          size="small"
          startIcon={<SearchIcon sx={{ fontSize: "18px" }} />}
          onClick={() => {
            onSearch && onSearch(data);
          }}
        >
          {"Check with Real-time Search"}
        </Button>
      ) : null}
    </Box>
  );
};

export default React.memo(SummaryAirline);
